import { useQuery } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { gql } from "generated-graphql";
import {
  AmazonWadmItemsQuery,
  AmazonWadmItemsQueryVariables,
  AmazonWadmTotalsQuery,
  AmazonWadmTotalsQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { prettyPrintDate } from "util/dates";
import { buildWasteFacilityUrl } from "util/wasteFacilityUrl";

import { WadmTotals } from "./WadmTotals";

const AMAZON_WADM_ITEMS_QUERY = gql(`
  query AmazonWadmItems($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    amazonWadmItems(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        periodStart
        siteCode
        regulatoryId
        facilityId
        tenantId
        state
        wadmGeneratorStatus
        wasteStream
        wasteProfile
        vendorName
        totalWeightLbs
        itemCount
        containerType
        containerStatus
        shipmentStatus
        shipmentDate
        trackingIdManifest
        importedAt
      }
      count
    }
  }
`);

const AMAZON_WADM_TOTALS_QUERY = gql(`
  query AmazonWadmTotals($search: String) {
    amazonWadmItems(search: $search, page: 0, pageSize: 0) {
      totals {
        totalWeightLbs
        totalItemCount
        byProfile {
          wasteProfile
          totalWeightLbs
          totalItemCount
        }
      }
    }
  }
`);

type AmazonWadmRow = AmazonWadmItemsQuery["amazonWadmItems"]["items"][number];

export const AmazonWadm = () => {
  useBreadcrumb({ label: "Amazon Waste and Donation Management" });

  const [searchParams, setSearchParams] = useSearchParams();
  const omnisearch = searchParams.get("omnisearch") ?? undefined;

  const handleFilterByProfile = useCallback(
    (profile: string) => {
      setSearchParams((prev) => {
        const current = prev.get("omnisearch") ?? "";
        // Remove any existing wasteProfile: term (quoted or unquoted)
        const cleaned = current
          .replace(/wasteProfile:"[^"]*"/g, "")
          .replace(/wasteProfile:\S+/g, "")
          .replace(/\s+/g, " ")
          .trim();
        const profileTerm = `wasteProfile:"${profile}"`;
        const next = new URLSearchParams(prev);
        next.set("omnisearch", cleaned ? `${cleaned} ${profileTerm}` : profileTerm);
        return next;
      });
    },
    [setSearchParams]
  );

  const { data: totalsData, loading: totalsLoading } = useQuery<
    AmazonWadmTotalsQuery,
    AmazonWadmTotalsQueryVariables
  >(AMAZON_WADM_TOTALS_QUERY, { variables: { search: omnisearch }, skip: !omnisearch });

  const columns: OmnisearchGridColDef<AmazonWadmRow>[] = useMemo(
    () => [
      {
        field: "periodStart",
        headerName: "Month",
        flex: 0.8,
        filterKeyType: "date",
        dateRangeShortcuts: true,
        renderCell({ value }) {
          return value ? prettyPrintDate(value) : "";
        },
      },
      {
        field: "siteCode",
        headerName: "Site",
        flex: 0.7,
      },
      {
        field: "state",
        headerName: "State",
        flex: 0.5,
        filterKeyType: "text",
      },
      {
        field: "regulatoryId",
        headerName: "EPA ID",
        flex: 1,
        renderCell({ value, row }) {
          if (!value) return "";
          if (row.facilityId && row.tenantId) {
            return (
              <Typography
                noWrap
                variant="body2"
                component={Link}
                to={buildWasteFacilityUrl(row.tenantId, row.facilityId)}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {value}
              </Typography>
            );
          }
          return value;
        },
      },
      {
        field: "wadmGeneratorStatus",
        headerName: "WADM Status",
        flex: 0.8,
      },
      {
        field: "wasteStream",
        headerName: "Waste Stream",
        flex: 1.2,
      },
      {
        field: "wasteProfile",
        headerName: "Profile",
        flex: 1.2,
      },
      {
        field: "vendorName",
        headerName: "Vendor",
        flex: 1.2,
      },
      {
        field: "totalWeightLbs",
        headerName: "Weight (lbs)",
        flex: 0.9,
        type: "number",
      },
      {
        field: "itemCount",
        headerName: "Items",
        flex: 0.6,
        type: "number",
      },
      {
        field: "containerType",
        headerName: "Container",
        flex: 0.8,
      },
      {
        field: "containerStatus",
        headerName: "Container Status",
        flex: 0.9,
      },
      {
        field: "shipmentStatus",
        headerName: "Shipment Status",
        flex: 0.9,
      },
      {
        field: "trackingIdManifest",
        headerName: "Manifest #",
        flex: 1,
      },
    ],
    []
  );

  return (
    <Stack py={3}>
      <WadmTotals
        totals={totalsData?.amazonWadmItems.totals ?? null}
        loading={totalsLoading}
        onFilterByProfile={handleFilterByProfile}
      />
      <OmnisearchDataGrid<AmazonWadmItemsQuery, AmazonWadmItemsQueryVariables, AmazonWadmRow>
        persistenceKey="amazonWadmItems"
        isURLDriven
        columns={columns}
        dataQuery={AMAZON_WADM_ITEMS_QUERY}
        getItems={(data) => data.amazonWadmItems.items}
        getCount={(data) => data.amazonWadmItems.count}
        commandButtons={[
          <ExportDataButton
            key="export"
            exportType="amazon-wadm"
            fileName="amazon-wadm"
            filter={
              searchParams.get("omnisearch")
                ? { search: searchParams.get("omnisearch") }
                : undefined
            }
          />,
        ]}
      />
    </Stack>
  );
};
